import React, { useCallback, useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import PlayTable from "../PlayTable/PlayTable";
import { MYSTERY_HAND, mysteryHandReady } from "./mysteryHandData";
import { recordMysteryResult, contractMade } from "./mysteryResults";
import { loadSolution } from "../ProblemHands/solutionStore";
import { markPlayed, PH_CATEGORY } from "../ProblemHands/problemProgress";
import { savePracticeCompletion } from "../../store/actions/usersActions";
import "../ProblemHands/ProblemHands.css";
import "./MysteryHand.css";

const STRAIN_SYM = { S: "♠", H: "♥", D: "♦", C: "♣", N: "NT" };
const STRAIN_CLASS = { S: "mh-black", H: "mh-red", D: "mh-diamond", C: "mh-club", N: "" };
const SUIT_SYM_CLASS = { "♠": "mh-black", "♥": "mh-red", "♦": "mh-diamond", "♣": "mh-club" };

function withColoredSuits(text) {
  return String(text)
    .split(/([♠♥♦♣])/)
    .map((part, i) =>
      SUIT_SYM_CLASS[part] ? <span key={i} className={SUIT_SYM_CLASS[part]}>{part}</span> : <React.Fragment key={i}>{part}</React.Fragment>
    );
}

function callLabel(call) {
  if (!call) return null;
  if (call.kind === "pass") return <span className="ph-pass">P</span>;
  if (call.kind === "double") return <span className="ph-dbl">X</span>;
  if (call.kind === "redouble") return <span className="ph-rdbl">XX</span>;
  return (
    <span>
      {call.level}
      <span className={STRAIN_CLASS[call.strain]}>{STRAIN_SYM[call.strain]}</span>
    </span>
  );
}

function AuctionGrid({ auction, dealer }) {
  const COLS = ["W", "N", "E", "S"];
  const rows = [];
  let row = [];
  for (let i = 0; i < COLS.indexOf(dealer); i++) row.push(null);
  for (const entry of auction) {
    row.push(entry);
    if (row.length === 4) { rows.push(row); row = []; }
  }
  if (row.length) { while (row.length < 4) row.push(null); rows.push(row); }
  return (
    <table className="ph-auction">
      <thead><tr>{COLS.map((c) => <th key={c}>{c}</th>)}</tr></thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i}>{r.map((entry, j) => <td key={j}>{entry ? callLabel(entry.call) : ""}{entry && entry.alert ? <span className="ph-alert">*</span> : null}</td>)}</tr>
        ))}
      </tbody>
    </table>
  );
}

// `hand` defaults to the mystery hand; /just-play/month-hand/<id> passes one of
// this month's other play hands (same table, same results collection).
function MysteryHandPlay({ hand: handProp, uid, displayName, subscriptionActive, isAdmin, authReady, savePracticeCompletion }) {
  const hand = handProp || MYSTERY_HAND;
  const isMystery = hand.id === MYSTERY_HAND.id;
  // Opened inside the Game Plan page's "Play this hand" panel (an iframe): no back
  // link, no list of its own, and "Back to Game Plan" closes the panel instead of
  // loading that page inside itself. The panel is told when a result is saved so
  // the board beside it can update. The messages carry no data.
  const framed = typeof window !== "undefined" && window.self !== window.top;
  const tellParent = useCallback((type, extra) => {
    try { window.parent.postMessage({ type, ...(extra || {}) }, "*"); } catch (_) {}
  }, []);
  const selfPath = isMystery ? "/just-play/mystery-hand" : `/just-play/month-hand/${hand.id}`;
  const [tableKey, setTableKey] = useState(0); // bump to deal the hand again
  const [outcome, setOutcome] = useState(null); // { made, tricks, firstMake, saved, error }
  const savingRef = useRef(false);
  // A Game Plan hand that is also a problem hand: offer its solution once one is recorded.
  const [hasSolution, setHasSolution] = useState(false);
  useEffect(() => {
    let alive = true;
    setHasSolution(false);
    if (hand.problemId) {
      loadSolution(hand.problemId).then((s) => {
        if (alive) setHasSolution(!!(s && ((s.play && s.play.length) || s.videoUrl)));
      });
    }
    return () => { alive = false; };
  }, [hand.problemId]);

  // No "Made the contract" list on this page any more (Paul, 21 Sep 2026: "get rid of any list that says
  // number of people doing whatever"). Results are still saved; the Game Plan ladder scores from them.

  const handleResult = useCallback(
    async (record) => {
      const made = contractMade(record);
      const tricks = Number(record.declarerTricks) || 0;
      setOutcome({ made, tricks, firstMake: false, saved: false, error: null });
      // Also tick it on the Problem Hands list.
      if (hand.problemId) {
        markPlayed(hand.problemId);
        if (uid) savePracticeCompletion(uid, PH_CATEGORY, hand.problemId);
      }
      if (!uid || savingRef.current) return;
      savingRef.current = true;
      try {
        const entry = await recordMysteryResult(hand.id, uid, displayName, record);
        setOutcome({ made, tricks, firstMake: !!entry.firstMake, alreadyMade: entry.made && !entry.firstMake, saved: true, error: null });
        if (framed) tellParent("bc-month-result", { play: { id: hand.id, made: !!made } });
      } catch (e) {
        setOutcome({ made, tricks, firstMake: false, saved: false, error: e && e.message ? e.message : String(e) });
      } finally {
        savingRef.current = false;
      }
    },
    [hand.id, hand.problemId, uid, displayName, framed, tellParent, savePracticeCompletion]
  );

  const playAgain = useCallback(() => {
    setOutcome(null);
    setTableKey((k) => k + 1);
  }, []);

  const isLocalhost = typeof window !== "undefined" && /^(localhost|127\.0\.0\.1)/.test(window.location.hostname);
  const canView = isLocalhost || isAdmin || !!subscriptionActive;

  const head = (
    <Helmet>
      <title>{hand.title} · Bridge Champions</title>
      <meta name="robots" content="noindex" />
    </Helmet>
  );

  if (!authReady) {
    return <div className="ph-page">{head}<p>Checking access…</p></div>;
  }

  if (!canView) {
    return (
      <div className="ph-page ph-gate">
        {head}
        <h1>{isMystery ? "The mystery hand is for members" : "This hand is for members"}</h1>
        <p className="mh-gateLine">Play it against the computer.</p>
        {!uid ? <Link to={`/login?redirectTo=${selfPath}`} className="ph-btn ph-btn--primary">Already a member? Log in</Link> : null}
        <Link to={`/membership?redirectTo=${selfPath}`} className={uid ? "ph-btn ph-btn--primary" : "ph-btn"}>View membership</Link>
      </div>
    );
  }

  if (!mysteryHandReady(hand)) {
    return (
      <div className="ph-page">
        {head}
        {framed ? null : <div className="ph-backBar"><a href="/game-plan" className="ph-back">Game Plan</a></div>}
        <h1 className="ph-title">{withColoredSuits(hand.title)}</h1>
        <p className="mh-soon">This hand is being set up. Check back shortly.</p>
      </div>
    );
  }

  const { contract, lead, deal } = hand;
  const rankDisplay = lead.card.rank === "T" ? "10" : lead.card.rank;
  const over = outcome ? outcome.tricks - (contract.level + 6) : 0;

  return (
    <div className="ph-page ph-playPage mh-page">
      {head}
      <div className="ph-backBar ph-backBar--play">
        {framed ? <span /> : <a href="/game-plan" className="ph-back">Game Plan</a>}
        <h1 className="ph-title ph-title--play">{withColoredSuits(hand.title)}</h1>
        <span className="mh-contractLine">
          {contract.level}
          <span className={STRAIN_CLASS[contract.strain]}>{STRAIN_SYM[contract.strain]}</span> by South ·{" "}
          <span className={STRAIN_CLASS[lead.card.suit]}>{STRAIN_SYM[lead.card.suit]}{rankDisplay}</span> lead
        </span>
      </div>

      <PlayTable
        key={tableKey}
        embedded
        singleDeal
        defaultFast={false}
        problemSetup={{
          hands: deal.hands,
          dealer: deal.dealer,
          vul: deal.vul,
          auction: hand.auction,
          contract,
          lead,
          forcedPlays: hand.forcedPlays,
          playRules: hand.playRules,
        }}
        exitLabel="Play again"
        onExit={playAgain}
        onResult={handleResult}
      />

      {outcome ? (
        <div className={`mh-result ${outcome.made ? "mh-result--made" : "mh-result--down"}`}>
          <h3>
            {outcome.made ? "Made it" : `Down ${contract.level + 6 - outcome.tricks}`} · {contract.level}
            <span className={STRAIN_CLASS[contract.strain]}>{STRAIN_SYM[contract.strain]}</span>
            {outcome.made && over > 0 ? ` +${over}` : ""}
          </h3>
          <p>
            {outcome.tricks} tricks.{" "}
            {/* No "made it" list any more (Paul, 21 Sep 2026), so no talk of a board. */}
            {outcome.made ? (uid ? "" : "Sign in to save your result.") : "You can try as many times as you like."}
            {outcome.error ? <span className="mh-err"> Could not save this result ({outcome.error}).</span> : null}
          </p>
          <div className="mh-resultActions">
            <button className="ph-btn ph-btn--primary" onClick={playAgain}>{outcome.made ? "Play again" : "Try again"}</button>
            {hasSolution ? <a className="ph-btn" href={`/just-play/problem-hands/${hand.problemId}?solution=1`} target="_top">See the solution and video</a> : null}
            {/* An article's hand (closeLabel) closes back to the article, not to Game Plan. */}
            {framed
              ? <button className="ph-btn" onClick={() => tellParent("bc-month-close")}>{hand.closeLabel || "Back to Game Plan"}</button>
              : hand.closeLabel ? null : <a className="ph-btn" href="/game-plan">Back to Game Plan</a>}
          </div>
        </div>
      ) : null}

      {(hand.auction && hand.auction.length > 0) || (hand.intro && hand.intro.length > 0) ? (
      <div className="ph-playInfo">
        {hand.auction && hand.auction.length > 0 ? <AuctionGrid auction={hand.auction} dealer={deal.dealer} /> : null}
        {hand.auctionNote ? <p className="ph-auctionNote">{withColoredSuits(hand.auctionNote)}</p> : null}
        {hand.intro && hand.intro.length > 0 ? (
          <div className="ph-intro">
            {hand.intro.map((para, i) => <p key={i}>{withColoredSuits(para)}</p>)}
          </div>
        ) : null}
      </div>
      ) : null}

    </div>
  );
}

const mapStateToProps = (state) => ({
  uid: state.auth?.uid,
  displayName: state.auth?.displayName || "",
  subscriptionActive: state.auth?.subscriptionActive === true,
  isAdmin: state.auth?.a === true,
  // Signed in but the members record has not arrived yet = still checking (not locked out).
  authReady: state.auth?.authReady === true && (!state.auth?.uid || state.auth?.subscriptionKnown === true || state.auth?.a === true),
});

export default connect(mapStateToProps, { savePracticeCompletion })(MysteryHandPlay);
